<template>
  <div class="container">
    <div class="columns box-title is-12">
      <div class="column is-8">
        <h4 class="has-text-left">
          <i class="fal fa-edit"></i>
          {{ $t('shipmentUpdateRequests.page_title') }}
          <span class="countTotal">({{ totalRows }})</span>
        </h4>
      </div>
      <div class="column is-4 has-text-right">
        <button
            class="button export-button"
            :class="{'is-loading': exporting}"
            :disabled="exporting || loading"
            @click="exportExcel"
        >
          <i class="fal fa-file-excel"></i>&nbsp;{{ $t('shipmentUpdateRequests.export_button') }}
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="box">
      <div class="columns is-multiline">
        <div class="column is-12-mobile is-6-tablet is-2-desktop">
          <label class="label">{{ $t('shipmentUpdateRequests.filters.status') }}:</label>
          <select v-model="form.status" class="input" @change="onFilterChange">
            <option value="">{{ $t('shipmentUpdateRequests.filters.all_statuses') }}</option>
            <option v-for="s in statuses" :key="s" :value="s">{{ $t('shipmentUpdateRequests.status.' + s) }}</option>
          </select>
        </div>
        <div class="column is-12-mobile is-6-tablet is-3-desktop">
          <label class="label">{{ $t('shipmentUpdateRequests.filters.branch') }}:</label>
          <model-list-select
              v-model="form.branch_id"
              :list="branchList"
              option-text="name"
              option-value="id"
              :placeholder="$t('shipmentUpdateRequests.filters.all_branches')"
          ></model-list-select>
          <p class="hint">{{ $t('shipmentUpdateRequests.filters.branch_hint') }}</p>
        </div>
        <div class="column is-12-mobile is-6-tablet is-2-desktop">
          <label class="label">{{ $t('shipmentUpdateRequests.filters.from') }}:</label>
          <input v-model="form.from" class="input" type="date" :max="form.to || undefined" @change="onFilterChange"/>
        </div>
        <div class="column is-12-mobile is-6-tablet is-2-desktop">
          <label class="label">{{ $t('shipmentUpdateRequests.filters.to') }}:</label>
          <input v-model="form.to" class="input" type="date" :min="form.from || undefined" @change="onFilterChange"/>
        </div>
        <div class="column is-12-mobile is-12-tablet is-3-desktop">
          <label class="label">{{ $t('shipmentUpdateRequests.filters.search') }}:</label>
          <input
              v-model="form.search"
              class="input"
              type="text"
              maxlength="50"
              :placeholder="$t('shipmentUpdateRequests.filters.search_placeholder')"
              @input="onSearchInput"
          />
        </div>
      </div>
    </div>

    <!-- List -->
    <div class="center">
      <div v-if="loading" class="state-panel">
        <Loading/>
        <p>{{ $t('shipmentUpdateRequests.loading') }}</p>
      </div>

      <div v-else-if="listError" class="state-panel error-panel">
        <p>{{ listError }}</p>
        <button class="button" @click="loadList(currentPage)">{{ $t('shipmentUpdateRequests.retry') }}</button>
      </div>

      <div v-else-if="rows.length === 0" class="state-panel">
        <p>{{ $t('shipmentUpdateRequests.empty_state') }}</p>
      </div>

      <vs-table v-else>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t('shipmentUpdateRequests.columns.requested_at') }}</vs-th>
            <vs-th>{{ $t('shipmentUpdateRequests.columns.shipment_number') }}</vs-th>
            <vs-th>{{ $t('shipmentUpdateRequests.columns.requester') }}</vs-th>
            <vs-th>{{ $t('shipmentUpdateRequests.columns.changes') }}</vs-th>
            <vs-th>{{ $t('shipmentUpdateRequests.columns.end_branch') }}</vs-th>
            <vs-th>{{ $t('shipmentUpdateRequests.columns.status') }}</vs-th>
            <vs-th>{{ $t('shipmentUpdateRequests.columns.processed') }}</vs-th>
            <vs-th>{{ $t('shipmentUpdateRequests.columns.created_pin') }}</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr v-for="row in rows" :key="row.id">
            <vs-td><small>{{ row.requested_at || placeholder }}</small></vs-td>
            <vs-td><span class="bill-number">{{ row.shipment_number || placeholder }}</span></vs-td>
            <vs-td>
              {{ row.requester_name || placeholder }}<br/>
              <small class="has-text-grey">{{ row.requester_tel }}</small>
            </vs-td>
            <vs-td>
              <span v-for="c in row.changes" :key="c" class="tag is-info is-light change-tag">
                {{ $t('shipmentUpdateRequests.changes.' + c) }}
              </span>
            </vs-td>
            <vs-td>
              {{ branchName(row.old_end_branch) }}
              <template v-if="row.new_end_branch">
                <i class="fal fa-long-arrow-right"></i> {{ branchName(row.new_end_branch) }}
              </template>
            </vs-td>
            <vs-td>
              <span class="tag" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
            </vs-td>
            <vs-td>
              <template v-if="row.processed_by || row.processed_at">
                {{ processorName(row.processed_by) }}<br/>
                <small class="has-text-grey">{{ row.processed_at }}</small>
              </template>
              <template v-else>{{ placeholder }}</template>
            </vs-td>
            <vs-td><small>{{ pinLabel(pinSide(row, 'created')) || placeholder }}</small></vs-td>
            <vs-td>
              <button class="button is-small view-button" @click="openDetail(row)">
                <i class="fal fa-eye"></i>&nbsp;{{ $t('shipmentUpdateRequests.view') }}
              </button>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <pagination
              v-if="paginator && paginator.last_page > 1"
              :pagination="paginator"
              :offset="10"
              @paginate="changePage"
          ></pagination>
        </template>
      </vs-table>
    </div>

    <!-- Detail (read only) -->
    <vs-dialog v-model="detailOpen" width="760px">
      <div class="card-form detail-card">
        <h5 class="dialog-title">{{ $t('shipmentUpdateRequests.detail.title') }}</h5>

        <div v-if="detailLoading" class="state-panel small-panel"><Loading/></div>
        <div v-else-if="detailError" class="notification is-danger is-light">{{ detailError }}</div>

        <template v-else-if="detail">
          <div class="detail-head">
            <span class="bill-number">{{ detail.shipment_number || placeholder }}</span>
            <span class="tag" :class="statusClass(detail.status)">{{ statusLabel(detail.status) }}</span>
          </div>
          <p class="is-size-7 has-text-grey">
            {{ $t('shipmentUpdateRequests.detail.requested', {at: detail.requested_at || placeholder}) }}
            · {{ detail.requester_name || placeholder }} {{ detail.requester_tel }}
          </p>

          <table class="table is-fullwidth is-narrow compare-table">
            <thead>
            <tr>
              <th></th>
              <th>{{ $t('shipmentUpdateRequests.detail.old') }}</th>
              <th>{{ $t('shipmentUpdateRequests.detail.new') }}</th>
              <th>{{ $t('shipmentUpdateRequests.detail.current') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="line in compareLines" :key="line.key" :class="{'is-changed': line.changed}">
              <th>{{ $t('shipmentUpdateRequests.detail.' + line.key) }}</th>
              <td>{{ line.old }}</td>
              <td>{{ line.changed ? line.new : placeholder }}</td>
              <td>{{ line.current }}</td>
            </tr>
            </tbody>
          </table>

          <div class="columns is-multiline is-mobile detail-grid">
            <div class="column is-6">
              <label class="label">{{ $t('shipmentUpdateRequests.detail.customer_remark') }}</label>
              <p>{{ detail.customer_remark || placeholder }}</p>
            </div>
            <div class="column is-6">
              <label class="label">{{ $t('shipmentUpdateRequests.detail.process_remark') }}</label>
              <p>{{ detail.process_remark || placeholder }}</p>
            </div>
            <div class="column is-6">
              <label class="label">{{ $t('shipmentUpdateRequests.detail.processed_by') }}</label>
              <p>{{ detailProcessorName }}</p>
            </div>
            <div class="column is-6">
              <label class="label">{{ $t('shipmentUpdateRequests.detail.processed_at') }}</label>
              <p>{{ detail.processed_at || placeholder }}</p>
            </div>
            <div class="column is-6">
              <label class="label">{{ $t('shipmentUpdateRequests.detail.created_pin') }}</label>
              <p>{{ pinLabel(pinSide(detail, 'created')) || placeholder }}</p>
            </div>
            <div class="column is-6">
              <label class="label">{{ $t('shipmentUpdateRequests.detail.cancelled_pin') }}</label>
              <p>{{ pinLabel(pinSide(detail, 'cancelled')) || placeholder }}</p>
            </div>
          </div>
        </template>

        <div class="dialog-actions">
          <button class="button" @click="detailOpen = false">{{ $t('shipmentUpdateRequests.close') }}</button>
        </div>
      </div>
    </vs-dialog>
  </div>
</template>

<script>
import Loading from '../../Loading';
import pagination from '../../../Plugins/Paginations/Pagination';
import {ModelListSelect} from 'vue-search-select';
import {
  downloadExport,
  errorMessage,
  fetchBranchOptions,
  fetchUpdateRequest,
  fetchUpdateRequests
} from '@/services/shipmentUpdateRequestAdminService';

const PER_PAGE = 20;

export default {
  name: 'ShipmentUpdateRequests',
  components: {Loading, pagination, ModelListSelect},
  data() {
    return {
      form: {
        status: '',
        branch_id: '',
        from: this.moment().startOf('month').format('YYYY-MM-DD'),
        to: this.moment().format('YYYY-MM-DD'),
        search: '',
      },
      statuses: ['pending', 'approved', 'rejected', 'cancelled'],
      branches: [],
      rows: [],
      paginator: null,
      loading: false,
      listError: '',
      listRequestId: 0,
      searchDebounceTimer: null,
      exporting: false,
      detailOpen: false,
      detailLoading: false,
      detailError: '',
      detail: null,
      detailRow: null,
      detailRequestId: 0,
      alive: true,
    };
  },
  computed: {
    placeholder() {
      return this.$t('shipmentUpdateRequests.placeholder');
    },
    branchList() {
      return [{id: '', name: this.$t('shipmentUpdateRequests.filters.all_branches')}, ...this.branches];
    },
    totalRows() {
      return this.paginator && this.paginator.total ? this.paginator.total : this.rows.length;
    },
    currentPage() {
      return this.paginator && this.paginator.current_page ? this.paginator.current_page : 1;
    },
    // The detail carries a bare id; the clicked list row's name is used only while it names the SAME
    // processor — a request decided after the list loaded shows `#id`, never a blank beside "approved".
    detailProcessorName() {
      const id = this.detail ? this.detail.processed_by : null;
      if (id === null || id === undefined) return this.placeholder;
      const listed = this.detailRow && this.detailRow.processed_by;
      return this.processorName(listed && Number(listed.id) === Number(id) ? listed : {id, name: null});
    },
    compareLines() {
      const d = this.detail;
      if (!d) return [];
      const current = d.current || {};
      return [
        {
          key: 'receiver_name', old: this.text(d.old_receiver_name), new: this.text(d.new_receiver_name),
          current: this.text(current.receiver_name), changed: d.new_receiver_name !== null && d.new_receiver_name !== undefined,
        },
        {
          key: 'receiver_tel', old: this.text(d.old_receiver_tel), new: this.text(d.new_receiver_tel),
          current: this.text(current.receiver_tel), changed: d.new_receiver_tel !== null && d.new_receiver_tel !== undefined,
        },
        {
          key: 'end_branch', old: this.branchName(d.old_end_branch), new: this.branchName(d.new_end_branch),
          current: this.branchName(current.end_branch), changed: !!d.new_end_branch,
        },
        {
          key: 'cod_amount', old: this.money(d.old_cod_amount), new: this.money(d.new_cod_amount),
          current: this.money(current.cod_amount), changed: d.new_cod_amount !== null && d.new_cod_amount !== undefined,
        },
      ];
    },
  },
  watch: {
    // model-list-select has no change event; a picked branch reloads page 1.
    'form.branch_id'() {
      this.onFilterChange();
    },
  },
  methods: {
    filterParams() {
      return {
        status: this.form.status,
        branch_id: this.form.branch_id,
        from: this.form.from,
        to: this.form.to,
        search: (this.form.search || '').trim(),
      };
    },
    validRange() {
      if (this.form.from && this.form.to && this.form.from > this.form.to) {
        this.$notification.OpenNotification_Error('top-right', 'warning', 3000,
            this.$t('shipmentUpdateRequests.error_range'));
        return false;
      }
      return true;
    },
    async loadBranches() {
      try {
        this.branches = await fetchBranchOptions();
      } catch (e) {
        this.branches = [];
      }
    },
    async loadList(page = 1) {
      if (!this.validRange()) return;

      const requestId = ++this.listRequestId;
      this.loading = true;
      this.listError = '';
      try {
        const {rows, meta} = await fetchUpdateRequests({...this.filterParams(), per_page: PER_PAGE, page});
        if (requestId !== this.listRequestId || !this.alive) return;
        this.rows = rows;
        this.paginator = meta;
      } catch (e) {
        const msg = await errorMessage(e, this.$t('shipmentUpdateRequests.error_loading'));
        if (requestId === this.listRequestId && this.alive) {
          this.rows = [];
          this.paginator = null;
          this.listError = msg;
        }
      } finally {
        if (requestId === this.listRequestId && this.alive) this.loading = false;
      }
    },
    onFilterChange() {
      this.loadList(1);
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => this.loadList(1), 350);
    },
    changePage(page) {
      this.loadList(page);
    },
    async exportExcel() {
      if (this.exporting || !this.validRange()) return;
      this.exporting = true;
      try {
        await downloadExport(this.filterParams());
      } catch (e) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 5000,
            await errorMessage(e, this.$t('shipmentUpdateRequests.error_export')));
      } finally {
        this.exporting = false;
      }
    },
    async openDetail(row) {
      const requestId = ++this.detailRequestId;
      this.detailRow = row;
      this.detail = null;
      this.detailError = '';
      this.detailLoading = true;
      this.detailOpen = true;
      try {
        const detail = await fetchUpdateRequest(row.id);
        if (requestId === this.detailRequestId && this.alive) this.detail = detail;
      } catch (e) {
        const msg = await errorMessage(e, this.$t('shipmentUpdateRequests.error_detail'));
        if (requestId === this.detailRequestId && this.alive) this.detailError = msg;
      } finally {
        if (requestId === this.detailRequestId && this.alive) this.detailLoading = false;
      }
    },
    text(value) {
      return value === null || value === undefined || value === '' ? this.placeholder : value;
    },
    money(value) {
      if (value === null || value === undefined || value === '') return this.placeholder;
      const n = Number(value);
      return Number.isFinite(n)
          ? n.toLocaleString('en-US', {maximumFractionDigits: 2}) + ' ' + this.$t('shipmentUpdateRequests.kip_unit')
          : String(value);
    },
    // A branch whose row is gone stays visible as `#id` (the Excel does the same).
    branchName(branch) {
      if (!branch) return this.placeholder;
      return branch.name || '#' + branch.id;
    },
    processorName(processedBy) {
      if (!processedBy) return this.placeholder;
      return processedBy.name || '#' + processedBy.id;
    },
    pinSide(record, side) {
      return record && record.pin_confirmation ? record.pin_confirmation[side] : null;
    },
    // Same wording as the Excel's PIN columns (backend ShipmentUpdateRequestExportRows::pinName).
    pinLabel(pin) {
      if (!pin) return '';
      if (pin.type === 'normal') return this.$t('shipmentUpdateRequests.pin.normal');
      if (pin.type !== 'special') return pin.type;
      return pin.label !== null && pin.label !== undefined
          ? this.$t('shipmentUpdateRequests.pin.special', {label: pin.label})
          : this.$t('shipmentUpdateRequests.pin.special_unlabelled', {id: pin.special_pin_id});
    },
    statusLabel(status) {
      return this.statuses.includes(status) ? this.$t('shipmentUpdateRequests.status.' + status) : status;
    },
    statusClass(status) {
      return {
        pending: 'is-warning',
        approved: 'is-success',
        rejected: 'is-danger is-light',
        cancelled: 'is-light',
      }[status] || 'is-light';
    },
  },
  created() {
    this.loadBranches();
    this.loadList(1);
  },
  beforeDestroy() {
    this.alive = false;
    clearTimeout(this.searchDebounceTimer);
  },
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 40px;
}

.export-button {
  background: #22c55e;

  &:hover {
    background: #16a34a;
  }
}

.view-button {
  background: #34495e;

  &:hover {
    background: #2b3a4a;
  }
}

.hint {
  font-size: 12px;
  color: #777;
  margin-top: 4px;
}

.bill-number {
  font-family: monospace;
  font-weight: 600;
}

.change-tag {
  margin: 0 4px 4px 0;
}

.state-panel {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  color: #555;
}

.small-panel {
  min-height: 120px;
}

.error-panel {
  border-color: #f4b5b5;
  color: #b42318;
}

.card-form {
  width: 720px;
  max-width: 100%;
  text-align: left;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
}

.detail-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.compare-table {
  margin-top: 12px;

  tr.is-changed td:nth-child(3) {
    font-weight: 600;
    color: #195bff;
  }
}

.detail-grid .label {
  font-size: 13px;
  margin-bottom: 2px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
